import { useState } from "react";
import { Loader2, Pencil, Trash2, X } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { cn } from "~/lib/utils";

interface ConversationShortcutRowProps {
  index: number;
  label: string;
  url: string;
  editing: boolean;
  saving: boolean;
  disabled?: boolean;
  validateUrl: (url: string) => boolean;
  onEdit: () => void;
  onSave: (label: string, url: string) => void;
  onCancel: () => void;
  onDelete: () => void;
}

const ConversationShortcutRow = ({
  index,
  label,
  url,
  editing,
  saving,
  disabled,
  validateUrl,
  onEdit,
  onSave,
  onCancel,
  onDelete,
}: ConversationShortcutRowProps) => {
  // Draft state is initialized from props on mount.
  // Parent re-keys this component on edit toggle to reset draft state.
  const [draftLabel, setDraftLabel] = useState(label);
  const [draftUrl, setDraftUrl] = useState(url);
  const [urlInvalid, setUrlInvalid] = useState(false);

  const handleSave = () => {
    if (draftUrl !== "" && !validateUrl(draftUrl)) {
      setUrlInvalid(true);
      return;
    }
    setUrlInvalid(false);
    onSave(draftLabel, draftUrl);
  };

  if (editing) {
    return (
      <div className="py-3">
        <div className="flex items-start gap-2">
          <div className="flex-1 space-y-1.5">
            <Input
              placeholder="Label"
              value={draftLabel}
              onChange={(e) => setDraftLabel(e.target.value)}
              maxLength={100}
              disabled={saving}
              className="h-9"
              autoFocus
            />
            <Input
              placeholder="Paste a featured conversation link"
              value={draftUrl}
              onChange={(e) => {
                setDraftUrl(e.target.value);
                setUrlInvalid(false);
              }}
              maxLength={2048}
              disabled={saving}
              className={cn("h-9", urlInvalid && "border-destructive")}
            />
            {urlInvalid && (
              <div className="flex items-center gap-1 text-destructive">
                <X className="size-3.5 shrink-0" />
                <span className="text-xs">URL must be a featured conversation link</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0 mt-1.5">
            <div className="size-8 flex items-center justify-center opacity-30" aria-hidden="true">
              <Pencil className="size-4" />
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              onClick={onDelete}
              disabled={disabled || saving}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <Button size="sm" onClick={handleSave} disabled={saving || draftUrl.trim() === ""}>
            {saving && <Loader2 className="size-4 mr-1 animate-spin" />}
            Save
          </Button>
          <Button variant="outline" size="sm" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start justify-between py-3">
      <div className="min-w-0 flex-1 mr-2">
        <p className="text-sm font-medium">{label || `Shortcut ${index + 1}`}</p>
        <p className="text-sm text-muted-foreground line-clamp-1">{url}</p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button variant="ghost" size="icon" className="size-8" onClick={onEdit} disabled={disabled}>
          <Pencil className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          onClick={onDelete}
          disabled={disabled}
        >
          <Trash2 className="size-4" />
        </Button>
      </div>
    </div>
  );
};

export default ConversationShortcutRow;
